import React from 'react';
import { useState, useEffect, useFormContext, Hooks, Code, Form, Field, Name, Title, Info, Actions, Toast, API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { MediaList, MediaUpload, useLogoImage } from 'helpers/ImageImports';
import { Company } from 'models/CompanyModel';
import { SingleSelectList } from 'components/common/MultiSelectList';

const CompanyForm: React.FC = () => {
  const width = { view: 20, open: 20, modify: 20 };
  const [formId, formName] = [Code.Form.Id.COMPANY, Code.Form.Name.COMPANY];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [cityOptions, setCityOptions] = Hooks.Form.Options();
  const [serverOptions, setServerOptions] = Hooks.Form.Options();
  const [databaseOptions, setDatabaseOptions] = Hooks.Form.Options();
  const [formData, setFormData] = useState<Company>({});
  const [fileList, setFileList] = useState<MediaList>({});
  const [featureList, setFeatureList] = Hooks.Form.Options();
  const [selectedFeature, setSelectedFeature] = useState<Set<string>>(new Set());
  const [featureFilter, setFeatureFilter] = useState<string>('');
  
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const {logoImage, handleLogoImageChange, handleAttachLogoImage, handleDeleteLogoImage } = useLogoImage(fileList, setFileList, setLogoUrl, setError);
 
  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
  
  useEffect(() => {
    Files.Json.API(Code.File.CITY, Code.Option.Common.SELECT, setCityOptions, setError);
    Files.Json.Http(Code.File.SERVER, Code.Option.Common.SELECT, setServerOptions, setError);
    Files.Json.Http(Code.File.DATABASE, Code.Option.Common.SELECT, setDatabaseOptions, setError);
    Files.Json.Http(Code.File.FEATURE, Code.Option.Common.NOTHING, setFeatureList, setError);
    
    API.Data.GetByKeyId<Company>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
        setActiveId(data.company_active); 
        Files.List.ParseFile(data.file_list, setFileList);
        const formData = await Form.Map.Data(data);
        setFormData((prevData) => ({
          ...prevData,
          ...formData,
        }));
        setSelectedFeature(Utility.Convert.SetString(data.feature_list))
      }})
      setLoading(false);
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;

    const finalData = Utility.Sanitize.FormData({
      ...formData,
      company_id: Utility.Storage.CompanyId(),
      location_id: Utility.Storage.LocationId(),
      entry_id: Utility.Storage.CompanyId(),
      company_phone: Utility.Convert.Number(formData.company_phone),
      file_list: JSON.stringify(Files.List.Clean(fileList)),
      feature_list: JSON.stringify(Array.from(selectedFeature))
    });

    API.Data.Save(formName, keyId, finalData, setMessage)

  };

  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} formName={formName} formId={formId} Privilege={Privilege} />
          <Control.Tab.Header titles={["Details", "Images", "Feature"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
 
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Box>
                <Field.Text id={Name.Company.Name} value={formData.company_name} title="Company Name" onChange={(e) => Form.Field.Change(e, formData, setFormData)} require={true} />
                <Field.Text id={Name.Company.LegalName} value={formData.company_legal_name} title="Company Legal Name" onChange={(e) => Form.Field.Change(e, formData, setFormData)}  />
              </Control.Box>
              <Control.Row>
                <Field.Text id={Name.Company.Phone} value={Utility.Format.Phone(formData.company_phone)} title={Title.Phone} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} />
                <Field.Text id={Name.Company.Email} value={formData.company_email} title={Title.Email} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} require={true} />
              </Control.Row>
              <Control.Box>
                <Field.Text id={Name.Company.Website} value={formData.company_website} title={Title.Website} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
                <Field.Area id={Name.Company.Address} value={formData.company_address} title={Title.Address} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Box>
              <Control.Row>
                <Field.Select id={Name.City.Id} value={formData.city_id} title={Title.City} options={cityOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)}  size={[0,50,1]} require={true} />
                <Field.Text id={Name.Company.Postal} value={formData.company_postal} title={Title.Postal} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab2} isActive={activeTab === Code.Tab.Tab2}>
              <MediaUpload label={Title.Logo} imageUrl={logoUrl} handleImageChange={handleLogoImageChange} handleAttachImage={handleAttachLogoImage} handleDeleteImage={handleDeleteLogoImage} summary={Info.LogoSize} />
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab3} isActive={activeTab === Code.Tab.Tab3}>
              <SingleSelectList allFormList={featureList} selectedItem={selectedFeature} setSelectedItem={setSelectedFeature}  listFilter={featureFilter} setListFilter={setFeatureFilter} />
            </Control.Tab.Content>

          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default CompanyForm;